import React, { useState } from 'react';
import { Users, Shield, Activity, Key, Blocks as Blockchain } from 'lucide-react';
import { UserManagement } from './UserManagement';
import { EncryptionManager } from './EncryptionManager';
import { BlockchainManager } from './BlockchainManager';
import { SystemMetrics } from './SystemMetrics';
import ComingSoonBanner from '../common/ComingSoonBanner';

type AdminView = 'users' | 'encryption' | 'blockchain' | 'metrics';

export const AdminDashboard: React.FC = () => {
  const [activeView, setActiveView] = useState<AdminView>('users');

  const navigationItems = [
    { id: 'users' as AdminView, icon: Users, label: 'User Management', count: 1247 },
    { id: 'encryption' as AdminView, icon: Key, label: 'Encryption Keys', count: 1247 },
    { id: 'blockchain' as AdminView, icon: Blockchain, label: 'Blockchain & P2P', count: 892 },
    { id: 'metrics' as AdminView, icon: Activity, label: 'System Metrics', alert: true },
  ];

  const renderActiveView = () => {
    switch (activeView) {
      case 'users':
        return <UserManagement />;
      case 'encryption':
        return <EncryptionManager />;
      case 'blockchain':
        return <BlockchainManager />;
      case 'metrics':
        return <SystemMetrics />;
      default:
        return <UserManagement />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Super Admin Dashboard"
        description="The admin interface will connect to live user data, real-time system metrics, actual encryption key management, and production blockchain operations for full system administration."
        currentPage="/admin"
        variant="compact"
        expectedRelease="very soon"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Super Admin Dashboard</h2>
          <p className="text-slate-600 mt-1">System administration and user management</p>
        </div>
        <div className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg">
          <Shield className="h-5 w-5" />
          <span className="font-medium">Super Admin Access</span>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <nav className="flex space-x-8">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-blue-500' : 'text-slate-400'}`} />
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{item.label}</span>
                  {item.count && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {item.count}
                    </span>
                  )}
                  {item.alert && (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Admin Content */}
      {renderActiveView()}
    </div>
  );
};
